import React, { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT, useDateFmt } from '../lang.js';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

/**
 * Tahsilat ekranı: önce PARA (hesap bazında toplamlar), sonra Stripe Connect
 * kurulumu. Sıra bilinçli — kurulum bir kez yapılır, kasa her gün sayılır.
 */
export default function Payments() {
  const t = useT();
  const fmt = useDateFmt();
  const [st, setSt] = useState(null);
  const [bal, setBal] = useState(null);
  const [tot, setTot] = useState(null);
  const [days, setDays] = useState(0); // 0 = bugün, 7 = son 7 gün
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const pct = (bps) => `${(bps / 100).toFixed(2)}%`;
  const load = () => api('/api/connect/status').then(setSt).catch((e) => setErr(errMessage(t, e)));

  useEffect(() => {
    // Gün başlangıcı CİHAZIN saatinde: kasiyer "bugün" derken kendi gününü
    // kastediyor; sunucu saat dilimi farkı kasa sayımında şaşırtır.
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    if (days) from.setDate(from.getDate() - days + 1);
    api(`/api/payments/totals?from=${from.toISOString()}`).then(setTot).catch(() => {});
  }, [days]);

  useEffect(() => {
    load();
    // Onboarding'den dönüşte Stripe'tan durumu TAZELE (webhook gecikebilir)
    if (new URLSearchParams(location.search).get('connect') === 'done') {
      api('/api/connect/sync', { method: 'POST' }).then(load).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  async function act(path) {
    setErr(''); setBusy(true);
    try {
      const out = await api(path, { method: 'POST' });
      if (out.url) location.href = out.url;
      else load();
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  if (err && !st) return <p className="err">{err}</p>;
  if (!st) return <PageSkeleton shape="tiles" />;

  const ready = st.status === 'COMPLETED';

  return (
    <>
      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('pay.totalsTitle')}</h2>
          <span className="spacer" />
          <button className={days === 0 ? 'chip on' : 'chip'} onClick={() => setDays(0)}>{t('pay.today')}</button>
          <button className={days === 7 ? 'chip on' : 'chip'} onClick={() => setDays(7)}>{t('pay.last7')}</button>
        </div>

        {!tot ? (
          <div className="sk-kpis" style={{ marginTop: 12 }} aria-hidden="true">
            {[0, 1, 2].map((i) => <span className="sk sk-kpi" key={i} />)}
          </div>
        ) : (
          <>
            <div className="tiles" style={{ marginTop: 12 }}>
              {tot.accounts.filter((a) => a.active || a.grossMinor).map((a) => (
                <div className="tile" key={a.id}>
                  <Icon n={a.icon} />
                  <span className="tname">{a.name}</span>
                  <b>{money(a.grossMinor, tot.currency)}</b>
                  <span className="muted">{t('pay.count', { n: a.count })}</span>
                </div>
              ))}
              {/* Çevrimiçi ödeme hesaba bağlı değil: parayı Stripe topluyor. */}
              <div className="tile">
                <Icon n="globe" />
                <span className="tname">{t('pay.online')}</span>
                <b>{money(tot.online.grossMinor, tot.currency)}</b>
                <span className="muted">{t('pay.count', { n: tot.online.count })}</span>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <b>{t('total')}</b>
              <b style={{ marginLeft: 'auto', fontSize: 18 }}>{money(tot.totalMinor, tot.currency)}</b>
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>{t('pay.totalsNote')}</p>
          </>
        )}
      </div>

      <div className="card">
        <h2>{t('pay.title')}</h2>
        {!st.enabled && <p className="muted" style={{ marginTop: 0 }}>{t('pay.noKeys')}</p>}
        <div className="row">
          <span className={`badge ${ready ? 'ok' : st.status === 'RESTRICTED' ? 'bad' : 'warn'}`}>{st.status}</span>
          {st.accountId && <span className="muted" style={{ fontSize: 12 }}>{st.accountId}</span>}
        </div>

        <p className="muted">{t('pay.explain', { rate: pct(st.commissionBps) })}</p>

        <div className="row">
          {!ready && (
            <button className="primary" disabled={busy || !st.enabled} onClick={() => act('/api/connect/onboarding')}>
              <Icon n="link" />{st.accountId ? t('pay.continueSetup') : t('pay.connectStripe')}
            </button>
          )}
          {st.accountId && (
            <>
              <button disabled={busy || !st.enabled} onClick={() => act('/api/connect/sync')}><Icon n="refresh" />{t('pay.syncStatus')}</button>
              <button disabled={busy || !st.enabled} onClick={async () => {
                setErr(''); setBusy(true);
                try { setBal(await api('/api/connect/balance')); }
                catch (e) { setErr(errMessage(t, e)); }
                finally { setBusy(false); }
              }}>
                <Icon n="wallet" />{t('pay.balance')}
              </button>
            </>
          )}
        </div>
        {err && <p className="err">{err}</p>}

        {bal && (
          <div style={{ marginTop: 14 }}>
            <div className="row">
              <span className="muted">{t('pay.available')}</span>
              <b style={{ marginLeft: 'auto' }}>
                {bal.available.map((b) => `${b.currency} ${(b.amountMinor / 100).toFixed(2)}`).join(' · ') || '—'}
              </b>
            </div>
            <div className="row">
              <span className="muted">{t('pay.pending')}</span>
              <span style={{ marginLeft: 'auto' }}>
                {bal.pending.map((b) => `${b.currency} ${(b.amountMinor / 100).toFixed(2)}`).join(' · ') || '—'}
              </span>
            </div>
            <table style={{ marginTop: 10 }}>
              <thead><tr><th>{t('pay.payout')}</th><th>{t('amount')}</th><th>{t('status')}</th><th>{t('pay.arrival')}</th></tr></thead>
              <tbody>
                {bal.payouts.length === 0 && <tr><td colSpan="4" className="muted">{t('pay.noPayouts')}</td></tr>}
                {bal.payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="muted" style={{ fontSize: 12 }}>{p.id}</td>
                    <td>{p.currency} {(p.amountMinor / 100).toFixed(2)}</td>
                    <td className="muted">{p.status}</td>
                    <td className="muted">{fmt.date(p.arrivalDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Bağlı hesapların Stripe panosu YOK (Stripe zararı üstlendiği için
                Express panosu açılamıyor) → bakiye ve ödemeler BURADA gösterilir. */}
            <p className="muted" style={{ marginBottom: 0 }}>{t('pay.payoutNote')}</p>
          </div>
        )}
      </div>

      {st.twint?.eligible && (
        <div className="card">
          <h2>TWINT</h2>
          <div className="row">
            <span className={`badge ${twintBadge(st.twint.status)}`}>{t(`pay.twint.${st.twint.status}`)}</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {t('pay.twintLimit', { max: fmt.number(st.twint.maxMinor / 100) })}
            </span>
          </div>
          <p className="muted">{t('pay.twintExplain')}</p>
          {st.twint.status !== 'active' && (
            <button className="primary" disabled={busy || !st.enabled || !st.accountId}
              onClick={() => act('/api/connect/twint/request')}>
              <Icon n="card" />{t('pay.twintRequest')}
            </button>
          )}
          {st.twint.status === 'pending' && (
            <p className="muted" style={{ marginBottom: 0 }}>{t('pay.twintPendingNote')}</p>
          )}
        </div>
      )}

      {!ready && st.enabled && (
        <div className="card">
          <h2>{t('pay.notReadyTitle')}</h2>
          <p className="muted" style={{ margin: 0 }}>{t('pay.notReadyNote')}</p>
        </div>
      )}
    </>
  );
}

function twintBadge(status) {
  if (status === 'active') return 'ok';
  if (status === 'pending' || status === 'unrequested') return 'warn';
  return 'bad';
}
